import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import GlobalStats from "./components/GlobalStats";
import TickerStrip from "./components/TickerStrip";
import MarketsTable from "./components/MarketsTable";
import SearchBar from "./components/SearchBar";
import CurrencySelect from "./components/CurrencySelect";
import RefreshTimer from "./components/RefreshTimer";
import Banner from "./components/Banner";
import {
  fetchMarkets,
  fetchGlobal,
  fetchMarketChart,
  msUntilBackoffEnds,
  isBackingOff,
} from "./api/coingecko";
import { connectTicker } from "./api/binance";
import useFetch from "./hooks/useFetch";
import useDebounce from "./hooks/useDebounce";
import useLocalStorage from "./hooks/useLocalStorage";
import useMediaQuery from "./hooks/useMediaQuery";
import { WS_COINS, INTERVALS, MARKETS_PER_PAGE } from "./utils/constants";
import "./App.css";

const CoinDetail = lazy(() => import("./components/CoinDetail"));

const SORT_DIR = { asc: "desc", desc: "asc" };

function sortValue(coin, key) {
  if (key === "market_cap_rank") return coin.market_cap_rank ?? Infinity;
  if (key === "name") return coin.name;
  const v = coin[key];
  return v == null || Number.isNaN(Number(v)) ? -Infinity : Number(v);
}

export default function App() {
  // ----- Persisted state (F9) -----
  const [currency, setCurrency] = useLocalStorage("ib:currency", "usd");
  const [watchlistIds, setWatchlistIds] = useLocalStorage("ib:watchlist", []);
  const [sortPref, setSortPref] = useLocalStorage("ib:sort", {
    key: "market_cap_rank",
    dir: "asc",
  });

  // ----- Ephemeral UI state -----
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | watchlist
  const [modalCoinId, setModalCoinId] = useState(null);
  const [offline, setOffline] = useState(
    typeof navigator !== "undefined" ? !navigator.onLine : false
  );
  const [wsStatus, setWsStatus] = useState("connecting");
  const [ticks, setTicks] = useState({});
  const [tickDirs, setTickDirs] = useState({});
  const [refreshCountdown, setRefreshCountdown] = useState(60);

  const debouncedQuery = useDebounce(query, INTERVALS.searchDebounceMs);
  const isCardLayout = useMediaQuery("(max-width: 760px)");

  // ----- Data layer (F1/F3, 60s polling, visibility-paused) -----
  const markets = useFetch((opts) => fetchMarkets({ ...opts, currency }), [currency], {
    pollMs: INTERVALS.marketsMs,
  });
  const global = useFetch((opts) => fetchGlobal(opts), [], {
    pollMs: INTERVALS.marketsMs,
  });
  const chart = useFetch(
    (opts) => fetchMarketChart({ ...opts, coinId: modalCoinId, currency }),
    [modalCoinId, currency],
    { enabled: modalCoinId != null }
  );

  const coins = useMemo(() => markets.data || [], [markets.data]);

  // ----- Watchlist (F5) -----
  const watchlist = useMemo(() => new Set(watchlistIds), [watchlistIds]);
  const toggleStar = useCallback(
    (id) => {
      setWatchlistIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    },
    [setWatchlistIds]
  );

  // ----- Sorting (F3) -----
  const onSort = useCallback(
    (key) => {
      setSortPref((prev) =>
        prev.key === key
          ? { key, dir: SORT_DIR[prev.dir] || "asc" }
          : { key, dir: key === "market_cap_rank" || key === "name" ? "asc" : "desc" }
      );
    },
    [setSortPref]
  );

  const sort = sortPref;

  // ----- Filtering + sorting (memoized, F3/F5) -----
  const visibleCoins = useMemo(() => {
    let list = coins;
    if (filter === "watchlist") list = list.filter((c) => watchlist.has(c.id));
    const q = debouncedQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.symbol.toLowerCase().includes(q)
      );
    }
    if (!sort.key) return list;
    const dir = sort.dir === "asc" ? 1 : -1;
    const key = sort.key;
    return [...list].sort((a, b) => {
      const va = sortValue(a, key);
      const vb = sortValue(b, key);
      if (typeof va === "string" || typeof vb === "string") {
        return String(va).localeCompare(String(vb)) * dir;
      }
      return (va - vb) * dir;
    });
  }, [coins, filter, watchlist, debouncedQuery, sort]);

  // ----- Live ticker (F2): WS with throttled state updates -----
  const ticksRef = useRef(ticks);
  ticksRef.current = ticks;

  useEffect(() => {
    const cleanup = connectTicker({
      onUpdate: (partial) => {
        setTicks((prev) => {
          const next = { ...prev, ...partial };
          const dirs = {};
          Object.keys(partial).forEach((id) => {
            const prevPrice = prev[id]?.price;
            dirs[id] =
              prevPrice == null || partial[id].price === prevPrice
                ? null
                : partial[id].price > prevPrice
                  ? "up"
                  : "down";
          });
          setTickDirs((d) => ({ ...d, ...dirs }));
          return next;
        });
      },
      onStatus: setWsStatus,
    });
    return cleanup;
  }, []);

  const tickerCoins = useMemo(
    () =>
      WS_COINS.map((c) => ({
        ...c,
        price: ticks[c.id]?.price,
        change: ticks[c.id]?.change,
        dir: tickDirs[c.id],
        delayed: wsStatus !== "live",
      })).filter((c) => c.price != null),
    [ticks, tickDirs, wsStatus]
  );

  // ----- Offline / online events (F8) -----
  const marketsRefresh = markets.refresh;
  const globalRefresh = global.refresh;
  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => {
      setOffline(false);
      marketsRefresh();
      globalRefresh();
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, [marketsRefresh, globalRefresh]);

  // ----- Rate-limit banner state -----
  const [rateLimited, setRateLimited] = useState(false);
  useEffect(() => {
    if (markets.error?.status === 429 || global.error?.status === 429) {
      setRateLimited(true);
    }
    if (!isBackingOff() && !markets.error && !global.error) {
      setRateLimited(false);
    }
  }, [markets.error, global.error]);

  const rateLimitRetryAt = useMemo(() => {
    if (!rateLimited) return null;
    const ms = msUntilBackoffEnds();
    return ms > 0 ? Date.now() + ms : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateLimited, markets.error, global.error]);

  const pollingPaused = offline || rateLimited;

  // ----- 60s countdown ring (F3), paused while hidden/rate-limited -----
  useEffect(() => {
    if (pollingPaused) return undefined;
    const id = setInterval(() => {
      if (document.hidden) return;
      setRefreshCountdown((s) => (s <= 1 ? INTERVALS.marketsMs / 1000 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [pollingPaused]);

  useEffect(() => {
    if (!pollingPaused) setRefreshCountdown(INTERVALS.marketsMs / 1000);
  }, [pollingPaused, currency]);

  // ----- Modal coin resolution -----
  const modalCoin = useMemo(
    () => coins.find((c) => c.id === modalCoinId) || null,
    [coins, modalCoinId]
  );

  const openModal = useCallback((id) => setModalCoinId(id), []);
  const closeModal = useCallback(() => setModalCoinId(null), []);

  const hasError = !!markets.error && !coins.length;

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <span className="brand-logo" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path
                d="M3 17.5 8.2 12l3.4 3.4L17 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="17" cy="10" r="2.2" fill="currentColor" />
            </svg>
          </span>
          <div className="brand-text">
            <h1>InsightBoard</h1>
            <span className="brand-sub mono">live crypto analytics</span>
          </div>
        </div>
        <div className="header-controls">
          <RefreshTimer
            secondsLeft={refreshCountdown}
            total={INTERVALS.marketsMs / 1000}
            paused={pollingPaused}
            pausedReason={offline ? "offline" : rateLimited ? "rate limited" : null}
          />
          <CurrencySelect value={currency} onChange={setCurrency} />
        </div>
      </header>

      {offline && (
        <Banner
          kind="offline"
          message="You're offline — showing the last cached market data."
        />
      )}
      {!offline && rateLimited && (
        <Banner
          kind="ratelimit"
          message="CoinGecko rate limit hit"
          retryInMs={rateLimitRetryAt}
          onRetry={markets.refresh}
        />
      )}
      {!offline && !rateLimited && hasError && (
        <Banner
          kind="error"
          message="Couldn't reach CoinGecko — check your connection."
          onRetry={markets.refresh}
        />
      )}

      <TickerStrip coins={tickerCoins} status={wsStatus} />

      <main className="app-main">
        <GlobalStats global={global.data?.data} currency={currency} loading={global.loading} />

        <div className="toolbar">
          <div className="tabs" role="tablist" aria-label="Filter coins">
            <button
              type="button"
              role="tab"
              aria-selected={filter === "all"}
              className={`tab ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              Top {MARKETS_PER_PAGE}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === "watchlist"}
              className={`tab ${filter === "watchlist" ? "active" : ""}`}
              onClick={() => setFilter("watchlist")}
            >
              Watchlist {watchlist.size > 0 ? `(${watchlist.size})` : ""}
            </button>
          </div>
          <SearchBar value={query} onChange={setQuery} />
        </div>

        {filter === "watchlist" && visibleCoins.length === 0 && !markets.loading ? (
          <div className="empty-state" role="status">
            <div className="empty-icon" aria-hidden="true">★</div>
            <p className="empty-title">Your watchlist is empty</p>
            <p className="empty-hint">
              Star any coin in the table to pin it here — it'll be saved on this
              device.
            </p>
          </div>
        ) : (
          <MarketsTable
            coins={visibleCoins}
            currency={currency}
            watchlist={watchlist}
            onToggleStar={toggleStar}
            onOpen={openModal}
            sort={sort}
            onSort={onSort}
            loading={markets.loading}
            isCardLayout={isCardLayout}
            error={hasError}
          />
        )}
      </main>

      <footer className="app-footer">
        <span>
          Data from <a href="https://www.coingecko.com" target="_blank" rel="noopener noreferrer">CoinGecko</a>
          {" · "}live stream via Binance WebSocket
        </span>
      </footer>

      {modalCoin && (
        <Suspense
          fallback={
            <div className="modal-backdrop">
              <div className="modal modal-loading" role="dialog" aria-modal="true" aria-label="Loading coin details">
                <span className="skeleton-pulse">Loading chart…</span>
              </div>
            </div>
          }
        >
          <CoinDetail
            coin={modalCoin}
            currency={currency}
            chart={chart.data}
            chartLoading={chart.loading}
            chartError={!!chart.error}
            onClose={closeModal}
          />
        </Suspense>
      )}
    </div>
  );
}
